import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
  getBillingAddress,
  saveBillingAddress,
  updateBillingAddress,
} from '../../CheckoutService'; // Import the service

const BillingAddress: React.FC = () => {
  const [billingDetails, setBillingDetails] = useState({
    id: '', // Add id field
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    county: '', // Changed state to county
    postCode: '',
    country: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchBillingDetails = () => {
    setLoading(true);
    getBillingAddress()
      .then((response) => {
        const data = response.data || {};
        setBillingDetails({
          id: data.id || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          address: data.address || '',
          city: data.city || '',
          county: data.county || '', // Use county here instead of state
          postCode: data.postCode || '', // Ensure postalCode is set here
          country: data.country || '',
        });
        setIsEditing(!data.firstName); // Enable editing if no billing address exists
      })
      .catch((error) =>
        console.error('Failed to fetch billing details:', error),
      )
      .finally(() => setLoading(false));
  };

  const handleSave = () => {
    if (isEditing) {
      if (
        !billingDetails.firstName ||
        !billingDetails.lastName ||
        !billingDetails.address
      ) {
        console.error('Some required fields are missing');
        return; // Prevent saving if some required fields are missing
      }

      console.log('Saving billing address:', billingDetails);

      if (billingDetails.firstName) {
        // Check if this is an update or a new address by checking if the ID exists or other differentiating property
        if (billingDetails.id) {
          // Update existing billing address
          updateBillingAddress(billingDetails)
            .then((response) => {
              console.log('Billing details updated successfully:', response);
              setIsEditing(false); // Disable editing after saving
            })
            .catch((error) => {
              console.error('Error updating billing details:', error);
            });
        } else {
          // Save new billing address
          saveBillingAddress(billingDetails)
            .then((response) => {
              console.log('Billing details saved successfully:', response);
              setIsEditing(false); // Disable editing after saving
            })
            .catch((error) => {
              console.error('Error saving billing details:', error);
            });
        }
      }
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleChange = (field) => (event) => {
    setBillingDetails({ ...billingDetails, [field]: event.target.value });
  };

  const handleCancel = () => {
    setIsEditing(false); // Revert to non-edit mode
  };

  useEffect(() => {
    fetchBillingDetails();
  }, []);

  return (
    <Box>
      {loading ? (
        <Typography variant="body1">Loading...</Typography>
      ) : (
        <>
          {/* <Typography variant="h6">Billing Address</Typography> */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={billingDetails.firstName}
                onChange={handleChange('firstName')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={billingDetails.lastName}
                onChange={handleChange('lastName')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={billingDetails.address}
                onChange={handleChange('address')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={billingDetails.city}
                onChange={handleChange('city')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="County" // Changed from "State" to "County"
                value={billingDetails.county} // Use county instead of state
                onChange={handleChange('county')} // Handle county field
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Postal Code"
                value={billingDetails.postCode}
                onChange={handleChange('postCode')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                value={billingDetails.country}
                onChange={handleChange('country')}
                fullWidth
                InputProps={{ readOnly: !isEditing }}
              />
            </Grid>
          </Grid>

          <Box marginTop={3}>
            {isEditing ? (
              <>
                <Button variant="contained" onClick={handleSave}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  sx={{ marginLeft: 2 }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="outlined" onClick={handleEdit}>
                Edit Billing Details
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default BillingAddress;
